// frontend/src/components/homepage/IgniteTag.js
//
// When a pulse in the mesh reaches a node, the node flares coral and a small
// label appears beside it naming a profession. It is the page's quietest and
// most load-bearing claim: the graph is not decoration, it is the product
// working, and each flare is one match.
//
// SUPPRESSION IS MOST OF THIS FILE, because a label that lands on top of the
// lead card does not read as ambient - it reads as a bug. A flare is skipped
// when it is:
//   * too close to the top edge (under the fixed navbar) or either side;
//   * inside the CARD's rect, generously padded - the card is about to grow
//     there, or is already sitting there.
// Everything left is where the prototype lets the tag land.
//
// It is off entirely on mobile (no room for it beside anything), off under
// motion off, and off while the hero is not on screen - the mesh keeps igniting
// down the page, and a label appearing over the pricing table would be nonsense.
//
// POSITION IS FIXED, not absolute-plus-scrollY as the prototype does it. The
// ignite event carries VIEWPORT coordinates, and the prototype's version only
// resolves correctly because its hero starts at the document origin; in the app
// the hero sits below a navbar inside a layout wrapper. Fixed is what the
// coordinates actually mean.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createFieldDeck } from "../../homepage/fieldDeck";
import { IGNITE_EVENT } from "../../homepage/meshConstants";

/**
 * How long a tag lives, entrance and exit included.
 *
 * The prototype toggles an `on` class and lets a CSS TRANSITION fade the tag in
 * and out (.7s in, the class removed at 3300ms, .7s out - a 4.0s arc). That
 * needs the element to mount un-lit and be lit a frame later, which in React is
 * a second state write and a second timer for something with no state worth
 * holding. The `hl-tagFade` / `hl-tagMove` keyframes carry that whole arc
 * instead - 17.5% in, hold, 17.5% out, i.e. the same .7s / 2.6s / .7s. They
 * play on mount, and the element is simply unmounted when they end.
 *
 * It was tried at 6000 and put back: with the cooldown below holding the slot
 * for the full life, 6s read as a long wait between labels rather than a label
 * you had time to read. The fix was the cooldown, not the length.
 *
 * LOAD-BEARING COUPLING: this number and the `animation-duration` on
 * `.hl-ignite-tag` in `src/homepage/homepage.css` (BOTH `hl-tagFade` and
 * `hl-tagMove`) are one value in two languages. Move one without the other and
 * the element is either mounted-but-faded - an invisible slot that the cooldown
 * below still counts as occupied - or unmounted mid-fade. Change all three in
 * the same commit; `igniteTag.test.js` scans the stylesheet and fails if they
 * drift.
 */
export const TAG_VISIBLE_MS = 4000;
/**
 * The shortest gap between two tags.
 *
 * Without it, EVERY accepted ignite replaced the tag on screen: a fresh key
 * remounts the element and restarts the keyframe, so the real dwell was
 * `min(TAG_VISIBLE_MS, gap to the next accepted ignite)` - measured at ~1.0-1.7s
 * against the declared 4s. The label read as flickering past rather than being
 * read. Equal to `TAG_VISIBLE_MS`, so a tag always lives its full declared life
 * and the next one may land the instant it ends - gapless, never overlapping.
 */
export const TAG_MIN_GAP_MS = TAG_VISIBLE_MS;
/** Clearance from the top of the viewport - below the fixed navbar. */
const TOP_MARGIN = 120;
/** Clearance from either side, so a tag is never half off screen. */
const SIDE_MARGIN = 40;
/** Padding around the lead card's own box - labels may live anywhere else in the hero. */
const STAGE_PAD = { left: 16, right: 16, top: 16, bottom: 16 };

/**
 * Is this point clear of everything the page has already put on screen?
 *
 * @param {{x: number, y: number}} point viewport coordinates.
 * @param {DOMRect|null} stageRect
 * @param {number} viewportWidth
 * @returns {boolean}
 */
export function isTagPositionClear(point, stageRect, viewportWidth) {
  const { x, y } = point;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if (y < TOP_MARGIN) return false;
  if (x < SIDE_MARGIN || x > viewportWidth - SIDE_MARGIN) return false;
  if (
    stageRect &&
    x > stageRect.left - STAGE_PAD.left &&
    x < stageRect.right + STAGE_PAD.right &&
    y > stageRect.top - STAGE_PAD.top &&
    y < stageRect.bottom + STAGE_PAD.bottom
  ) {
    return false;
  }
  return true;
}

/**
 * @param {object} props
 * @param {boolean} props.enabled
 * @param {string[]} props.fields the profession deck.
 * @param {React.RefObject} props.stageRef the lead card's surface.
 */
function IgniteTag({ enabled, fields, stageRef }) {
  const [tag, setTag] = useState(null);
  const hideTimerRef = useRef(null);
  /**
   * When the tag now on screen appeared, on the MONOTONIC clock.
   *
   * `performance.now()`, not `Date.now()`: a wall clock can step backwards (NTP,
   * a manual change, DST on some platforms) and a backwards step makes the
   * cooldown look unexpired for as long as the jump lasted - the tag freezes.
   * `-Infinity` rather than 0, because `performance.now()` starts AT 0 on a
   * fresh document, so a literal 0 is a real timestamp here.
   */
  const shownAtRef = useRef(-Infinity);
  const deck = useMemo(() => createFieldDeck(fields), [fields]);

  useEffect(() => {
    if (!enabled) {
      setTag(null);
      // The hero can leave and re-enter the viewport; a stamp from the last
      // visit must not suppress the first tag of the next one. Here ONLY, never
      // in the cleanup below: the cleanup also runs when `deck` or `stageRef`
      // merely changes identity (a caller passing an inline `fields` array is
      // enough), and clearing the stamp there would hand out a second tag
      // inside a live one's life on every such re-render.
      shownAtRef.current = -Infinity;
      return undefined;
    }

    const onIgnite = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      // FIRST, before the position test and before the deck is dealt from: an
      // ignite inside the cooldown is not a tag that failed to place, it is a
      // tag we are not taking, and it must not cost a label from the deck.
      if (performance.now() - shownAtRef.current < TAG_MIN_GAP_MS) return;
      const point = { x: detail.x, y: detail.y };
      const stageRect = stageRef?.current?.getBoundingClientRect() || null;
      if (!isTagPositionClear(point, stageRect, window.innerWidth)) return;

      const label = deck.next();
      if (!label) return;
      // A fresh key remounts the element and restarts the life keyframe. With
      // the cooldown above that can only happen once the previous tag's life
      // has run out, so the remount is now the start of a life rather than the
      // truncation of one.
      const shownAt = performance.now();
      shownAtRef.current = shownAt;
      setTag({ ...point, label, key: `${point.x}:${point.y}:${shownAt}` });
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setTag(null), TAG_VISIBLE_MS);
    };

    window.addEventListener(IGNITE_EVENT, onIgnite);
    return () => {
      window.removeEventListener(IGNITE_EVENT, onIgnite);
      clearTimeout(hideTimerRef.current);
    };
  }, [enabled, deck, stageRef]);

  if (!tag) return null;

  return (
    <div
      key={tag.key}
      className="hl-ignite-tag"
      aria-hidden="true"
      style={{ left: `${tag.x}px`, top: `${tag.y}px` }}
    >
      <i />
      {tag.label}
    </div>
  );
}

// `fields` is a module constant and `stageRef` a stable ref, so the hero's own
// re-renders never reach the tag; only `enabled` flips it.
export default React.memo(IgniteTag);
